/**
 * The three choosable AI personas on the /for-investors page. A visitor
 * picks who they want to talk to; that persona calls them back (outbound
 * via the AlfredVoice tenant, caller-id (414) 310-0078).
 *
 * `id` is the AlfredVoice persona/voice key AND the photo filename:
 * the real portrait lives at `/personas/{id}.{ext}` (committed under
 * frontend/public/personas/); PersonaCard falls back to the gradient +
 * icon until/if the photo is missing.
 *
 * Array order is the desktop layout order — Marcus · Sloan(center) · Mateo
 * — and Sloan is the flagship (visually emphasized, ties to the existing
 * VentureOS Alfred tenant persona "Sloan").
 */
import type { LucideIcon } from 'lucide-react';
import { Briefcase, Sparkles, Rocket } from 'lucide-react';

/** Display string for the outbound caller-id — the provisioned 414 DID
 *  (+14143100078) that each persona dials from. Shown in the callback form. */
export const VOICE_CALLBACK_CALLER_ID = '(414) 310-0078';

export interface InvestorPersona {
  /** AlfredVoice persona/voice key + photo filename stem */
  id: string;
  name: string;
  /** Short role descriptor shown above the name */
  tagline: string;
  /** Fallback icon (lucide) shown only until the photo loads */
  icon: LucideIcon;
  /** Tailwind from-/to- classes for the fallback gradient behind the photo */
  gradient: string;
  /** Visually emphasized centerpiece */
  flagship: boolean;
  /** Photo file extension under /personas/{id}.{ext} */
  ext: string;
}

export const INVESTOR_PERSONAS: InvestorPersona[] = [
  {
    id: 'marcus',
    name: 'Marcus',
    tagline: 'Seasoned partner',
    icon: Briefcase,
    gradient: 'from-slate-700 to-slate-900',
    flagship: false,
    ext: 'png',
  },
  {
    id: 'sloan',
    name: 'Sloan',
    tagline: 'Your VentureOS concierge',
    icon: Sparkles,
    gradient: 'from-apple-blue to-indigo-700',
    flagship: true,
    ext: 'png',
  },
  {
    id: 'mateo',
    name: 'Mateo',
    tagline: 'Founder-friendly operator',
    icon: Rocket,
    gradient: 'from-amber-600 to-rose-700',
    flagship: false,
    ext: 'png',
  },
];
